(ns app.front.view.admin.groups
  (:require [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.admin.subs :as admin-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.comm :as comm]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.front.session :as session]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema.core :as s]))


(rf/reg-event-fx
  ::fetch-groups
  (fn [_ [_ {:keys [group-id]}]]
    {:comm/command {:command    :admin/groups
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::entities/merge-entity-db]}}))

;; --------------------------
;; Dialog events and subs
;; --------------------------

(rc/reg-chain
  ::dialog-save

  (fn [_ [_ k]]
    (>evt [::admin-subs/dialog-edit k :disabled? true]))

  (fn [{:keys [db]} [_ k parent-group-id]]
    (case k
      :new-group (let [{:keys [group-name]} (:new-group db)]
                   {:comm/command {:command         :admin/create-new-group
                                   :data            {:group-name      group-name
                                                     :parent-group-id parent-group-id}
                                   :on-server-error [::dialog-server-error k]}})
      :add-user (let [{:keys [group-id email first-name last-name]} (:add-user db)]
                  {:comm/command {:command         :admin/add-user
                                  :data            {:parent-group-id parent-group-id
                                                    :user/email      email
                                                    :user/first-name first-name
                                                    :user/last-name  last-name
                                                    :user/groups     [group-id]}
                                  :on-server-error [::dialog-server-error k]}})))

  (fn [{:keys [db]} [_ k parent-group-id]]
    {:dispatch-n [[::fetch-groups {:group-id parent-group-id}]
                  [::admin-subs/dialog-close k]
                  (when (and (= k :add-user)
                             (= (-> db :add-user :email)
                                (-> db :session/user :user/email)))
                    [::session/restore-session])]}))

(rf/reg-event-fx ::remove-group
  (fn [_ [_ group-id]]
    {:comm/command {:command    :admin/remove-group
                    :data       (util/mmap group-id)
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx
  ::dialog-server-error
  (fn [_ [_ k error]]
    {:dispatch-n [[::admin-subs/dialog-edit k :disabled? false]
                  [:notifications/add :toast :error
                   (get comm/http-status->error-title error :error/error)
                   (get comm/http-status->error-description error :error/unknown)]]}))

(rf/reg-sub
  ::dialog-filled?
  :<- [::admin-subs/dialogs]
  (fn [data [_ k]]
    (case k
      :new-group (let [{:keys [group-domain domain-name domain-domain
                               group-name domain-id]}                 (:new-group data)]
                   (and (not (str/blank? group-name))
                        (or (not= group-domain "new-domain")
                            (not-any? str/blank? [domain-id domain-name domain-domain]))))
      :add-user (->> data :add-user vals (not-any? str/blank?)))))

;; --------------------------------
;; Group events and subs
;; --------------------------------

(rf/reg-event-fx
  ::new-group-open
  (fn [_ _]
    (>evt [::admin-subs/dialog-open :new-group {:group-name ""}])))

(rc/reg-chain ::rename-group
  (fn [_ [_ group-id new-name]]
    {:comm/command {:command :admin/rename-group
                    :data    {:group-id group-id
                              :new-name new-name}}})
  (fn [_ [_ group-id new-name]]
    (>evt [::entities/update-entity group-id {:group/name new-name}])))

(rf/reg-sub
  ::editing-group?
  (fn [db [_ group-id]]
    (get-in db [::editing-group group-id])))

(rf/reg-event-db
  ::toggle-editing-group
  (fn [db [_ group-id editing?]]
    (assoc-in db [::editing-group group-id] (boolean editing?))))

;; --------------------------
;; Admin events and subs
;; --------------------------

(rf/reg-event-fx
  ::add-user-open
  (fn [_ [_ group-id]]
    (>evt [::admin-subs/dialog-open :add-user {:group-id   group-id
                                               :email      ""
                                               :first-name ""
                                               :last-name  ""}])))

(rc/reg-chain
  ::remove-admin

  (fn [_ [_ group-id _ user-id]]
    {:comm/command {:command :admin/edit-user-groups
                    :data    {:remove  [group-id]
                              :add     []
                              :user-id user-id}}})
  (fn [{:keys [db]} [_ _ parent-group-id user-id]]
    {:dispatch-n [[::entities/remove-entity user-id]
                  [::fetch-groups {:group-id parent-group-id}]
                  (when (= user-id (-> db :session/user :db/id))
                    [::session/restore-session])]}))

;; --------------------------
;; Dialogs and modal bodies
;; --------------------------

(defn- dialog
  "Dialog with Cancel and Save buttons. The actual contents (modal body)
  is given via arguments."
  [{:keys [tr] :as options} {:keys [key focus-selector body
                                    title parent-group-id]}]
  (let [open?     (boolean (<sub [::admin-subs/dialog-data key :open?]))
        disabled? (boolean (<sub [::admin-subs/dialog-data key :disabled?]))
        cancel-fn #(>evt [::admin-subs/dialog-close key])]
    [ui-modal/composed-input-modal
     {:open                   open?
      :selector-primary-focus focus-selector
      :on-close               cancel-fn}
     (cc/modal-header {:close-modal cancel-fn
                       :title       title})

     (when open?
       (cc/modal-body
         {:class-name "overflow-visible"}
         [body options]))
     (cc/modal-footer
       {}
       (cc/button {:kind     :secondary
                   :disabled disabled?
                   :on-click cancel-fn}
                  (tr [:cancel]))
       (cc/button {:kind     :primary
                   :disabled (or disabled?
                                 (not (<sub [::dialog-filled? key])))
                   :on-click #(>evt [::dialog-save key parent-group-id])}
                  (tr [:save])))]))

(defn new-group-modal-body [{:keys [tr]}]
  (let [disabled? (boolean (<sub [::admin-subs/dialog-data :new-group :disabled?]))
        edit-fn   (fn [k]
                    (fn [e]
                      (rf/dispatch-sync [::admin-subs/dialog-edit :new-group k (.. e -target -value)])))]
    [:div.w-full.p-8
     [:div.bx--row.my-4
      [:div.bx--col-xs-12
       [ui/text-input {:id         "new-group-name"
                       :label-text (tr [:admin/group-name])
                       :disabled   disabled?
                       :on-change  (edit-fn :group-name)}]]]]))

(defn new-group-modal [{:keys [tr parent-group-id] :as options}]
  [dialog options {:key             :new-group
                   :focus-selector  "input#new-group-name"
                   :parent-group-id parent-group-id
                   :body            new-group-modal-body
                   :title           (tr [:admin/new-group])}])

(defn add-admin-modal-body [{:keys [tr]}]
  (let [disabled? (boolean (<sub [::admin-subs/dialog-data :add-user :disabled?]))
        edit-fn   (fn [k]
                    (fn [e]
                      (rf/dispatch-sync [::admin-subs/dialog-edit :add-user k (.. e -target -value)])))
        group-id  (<sub [::admin-subs/dialog-data :add-user :group-id])]
    [:div.w-full.p-8
     [:div
      [:span (tr [:admin/add-user-for]
                 [(:group/name (<sub [::entities/entity group-id]))])]
      [:span " "]
      [:span (tr [:user/add-user-help])]]
     [:div.bx--row.my-4
      [:div.bx--col-xs-12
       [ui/text-input {:id         "add-admin-email"
                       :label-text (tr [:party/email])
                       :disabled   disabled?
                       :on-change  (edit-fn :email)}]]]
     [:div.bx--row.my-4
      [:div.bx--col-xs-12
       [ui/text-input {:id         "add-admin-first-name"
                       :label-text (tr [:party/first-name])
                       :disabled   disabled?
                       :on-change  (edit-fn :first-name)}]]]
     [:div.bx--row.my-4
      [:div.bx--col-xs-12
       [ui/text-input {:id         "add-admin-last-name-"
                       :label-text (tr [:party/last-name])
                       :disabled   disabled?
                       :on-change  (edit-fn :last-name)}]]]]))

(defn add-admin-modal [{:keys [tr parent-group-id] :as options}]
  [dialog options {:key             :add-user
                   :parent-group-id parent-group-id
                   :focus-selector  "input#add-admin-email"
                   :body            add-admin-modal-body
                   :title           (tr [:admin/add-user])}])

;; -------------------------
;; Group table UI
;; -------------------------

(defn edit-button [{:keys [tr]} {:keys [group-id expand-fn expanded?]}]
  (let [editing? (<sub [::editing-group? group-id])]
    (when (and editing? (not expanded?))
      (>evt [::toggle-editing-group group-id (not editing?)]))
    [:div.flex.flex-1.justify-end
     (cc/button {:kind     (if editing? :secondary :primary)
                 :size     :small
                 :on-click (fn [_]
                             (>evt [::toggle-editing-group group-id (not editing?)])
                             (when (and (not editing?) (not expanded?))
                               (expand-fn)))}
                (tr [(if editing? :admin/stop-editing :admin/edit)]))]))

(rf/reg-sub ::group-users
  (fn [[_ group-id]]
    (rf/subscribe [::entities/entities
                   (filter (fn [e]
                             (some-> e :user/groups set
                                     (contains? group-id))))]))
  (fn [users _] users))

(rf/reg-sub ::can-remove?
  (fn [[_ group-id]]
    [(rf/subscribe [::group-users group-id])
     (rf/subscribe [::entities/entity-attr group-id :group/roles])
     (rf/subscribe [::entities/entity-attr group-id :group/type])])
  (fn [[users roles type] _]
    (and (empty? users)
         (empty? roles)
         (= type :group.type/user-group))))

(defn group-details [{:keys [tr parent-group-id]} group-id]
  (let [editing?        (<sub [::editing-group? group-id])
        group           (<sub [::entities/entity group-id])
        allow-user-add? (= :group.type/user-group (:group/type group))
        group-name      (:group/name group)
        admins          (<sub [::group-users group-id])]
    [:div
     [:div.bx--row
      [:div.pr-8.pb-4.bx--col-xs-12.bx--col-lg-6
       (if editing?
         [ui/text-input {:id            (str "user-group-name-" group-id)
                         :label-text    (tr [:admin/group-name])
                         :default-value group-name
                         :on-blur       (fn [e]
                                          (some->> (.. e -target -value)
                                                   str/trim
                                                   not-empty
                                                   (conj [::rename-group group-id])
                                                   >evt))}]
         [:<>
          [:div.bx--label (tr [:admin/group-name])]
          [:div.label-value group-name]])
       (when (and editing? (<sub [::can-remove? group-id]))
         [:div.mt-4
          [ui/modal-confirmation-button
           {:id                  (str "remove-group-" group-id)
            :trigger-button-opts {:kind :secondary
                                  :size :small
                                  :icon i/trash}
            :tr                  tr
            :trigger-button-text (tr [:group/remove])
            :on-yes-click        #(>evt [::remove-group group-id])}
           (tr [:group/really-remove] [group-name])]])]
      [:div.bx--col-xs-12.bx--col-lg-6
       [:div.bx--label (if allow-user-add? (tr [:admin/group-users]) "")]
       [:div.table
        (for-frag [{:keys [id fullname
                           email]} (->> admins
                                        (map (fn [{id         :db/id
                                                   email      :user/email
                                                   first-name :user/first-name
                                                   last-name  :user/last-name}]
                                               {:id       id
                                                :email    email
                                                :fullname (str first-name " " last-name)}))
                                        (sort-by :fullname))]
          [:div.table-row {:key id}
           [:div.table-cell.align-middle {:key (str "fullname-" id)} fullname]
           [:div.table-cell.align-middle.px-4 {:key (str "email-" id)} email]
           (when editing?
             [:div.table-cell.align-middle {:key (str "remove-" id)}
              [ui/modal-confirmation-button
               {:tr                tr
                :id                "remove-admin"
                :trigger-icon-opts {:icon        i/remove
                                    :class-name  "cursor-pointer"
                                    :fill        "#cc1b00"
                                    :description (tr [:admin/remove-admin])}
                :on-yes-click      #(>evt [::remove-admin group-id parent-group-id id])}
               [:div (tr [:admin/remove-admin-confirmation] [fullname email group-name])]]])])]
       (when (and editing? allow-user-add?)
         [:div.mt-2
          (cc/button {:kind             :primary
                      :size             :small
                      :icon             i/user-plus
                      :icon-description (tr [:admin/add-user])
                      :on-click         #(>evt [::add-user-open group-id])}
                     (tr [:admin/add-user]))])]]]))

(defn render-groups [{:keys [tr] :as options} props]
  (let [{:keys [rows headers getHeaderProps getRowProps expandRow]} (js->clj props :keywordize-keys true)]
    (r/as-element
      (cc/table-container
        {:class-name "w-full"}
        (cc/table
          {}
          (cc/table-head
            {}
            (cc/table-row
              {}
              (cc/table-expand-header)
              (for-frag [header headers
                         :let [header-props (js->clj (getHeaderProps #js {:header   (clj->js header)
                                                                          :children (:header header)})
                                                     :keywordize-keys true)]]
                (cc/table-header header-props))))
          (cc/table-body
            {}
            (for-frag [row rows
                       :let [row-id    (:id row)
                             group-id  (futil/parse-int row-id)
                             row-props (js->clj (getRowProps #js {:row                   (clj->js row)
                                                                  :expandIconDescription (tr [:carbon.table.row.expand])})
                                                :keywordize-keys true)
                             expanded? (:isExpanded row)]]
              [:<>
               (cc/table-expand-row
                 row-props
                 (for-frag [cell (:cells row)
                            :let [header (-> cell :info :header keyword)]]

                   (cc/table-cell
                     {:key (:id cell)}
                     (if (= header :edit)
                       [edit-button options {:group-id  group-id
                                             :expand-fn #(expandRow row-id)
                                             :expanded? expanded?}]
                       (:value cell)))))
               (when expanded?
                 (cc/table-expanded-row {:key      (str "expanded-" row-id)
                                         :col-span (inc (count headers))}
                                        [group-details options group-id]))])))))))

(defn stakeholder-groups [{:keys [tr parent parent-group-id] :as options}]
  (let [groups (<sub [::entities/entities {:entity/type :entity.type/group}])]
    (cond
      (nil? parent) (cc/data-table-skeleton)
      (seq groups)
      (cc/data-table {:is-sortable false
                      :headers     [{:key :group-name :header (tr [:admin/group-name])}
                                    {:key :domain-name :header (tr [:admin/domain-name])}
                                    {:key :edit :header ""}]
                      :rows        (->> groups
                                        (filter (p/fn-> :entity/parent :db/id (= parent-group-id)))
                                        (map (fn [g]
                                               {:id          (str (:db/id g))
                                                :group-name  (:group/name g)
                                                :domain-name (-> g :entity/parent :group/name)}))
                                        (sort-by :group-name))
                      :render      (partial render-groups options)})
      :else [:div])))


(defn view [match]
  (let [{:keys [group-id]} (-> match :parameters :path)
        tr                 (<sub [:tr])
        parent             (<sub [::entities/entity {:db/id group-id}])]
    [:div.cloudpermit-page.admin-page
     [:div.bx--row.page-heading.justify-between
      [:h1 (:group/name parent)]
      [:div.flex
       (when-let [grandparent-id (-> parent :entity/parent :db/id)]
         (cc/link {:href       (href/href :view/admin-groups {:group-id grandparent-id})
                   :class-name "bx--btn bx--btn--secondary mr-4"}
                  (cc/icon {:icon        i/arrow-up
                            :class-name  "mr-4"
                            :description ""})
                  (tr [:admin/parent-group])))
       (cc/button {:icon-position    :left
                   :icon             i/plus
                   :icon-description (tr [:admin/add-group])
                   :on-click         #(>evt [::new-group-open parent])}
                  (tr [:admin/add-group]))]]
     [:section.bx--row [stakeholder-groups {:tr tr :parent parent :parent-group-id group-id}]]
     [new-group-modal {:tr tr :parent parent :parent-group-id group-id}]
     [add-admin-modal {:tr tr :parent-group-id group-id}]]))

(def route
  {:name        :view/admin-groups
   :parameters  {:path {:group-id s/Int}}
   :view        view
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [params]
                                (>evt [::fetch-groups (:path params)]))}]})
